import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Stethoscope, Lightbulb, HeartHandshake, ShieldCheck } from "lucide-react";
import SiteNav from "@/components/site/SiteNav";
import SiteFooter from "@/components/site/SiteFooter";
import { useSeo } from "@/hooks/useSeo";

const VALUES = [
  { icon: Stethoscope, title: "Aus der Praxis, für die Praxis", text: "Der Bedarf für LAIzyDocu entstand direkt im Praxisalltag – aus dem täglichen Aufwand mit Papierformularen, Aufklärungsbögen und der Archivierung von Einwilligungen." },
  { icon: HeartHandshake, title: "Gemeinsam entwickelt", text: "LAIzyDocu wurde Hand in Hand mit einer Physiotherapie-Praxis konzipiert und erprobt. Jeder Schritt im Patientenflow wurde am realen Empfangstresen getestet und verfeinert." },
  { icon: ShieldCheck, title: "Datenschutz zuerst", text: "Weil Gesundheitsdaten besonders schützenswert sind, war DSGVO-Konformität von Anfang an kein Zusatz, sondern das Fundament: Verschlüsselung, Audit-Logs und deutsche Server." },
];

export default function About() {
  useSeo(
    "Über uns – LAIzyDocu: Aus der Praxis für die Praxis entwickelt",
    "LAIzyDocu entstand aus dem echten Bedarf einer Physiotherapie-Praxis und wurde gemeinsam mit ihr entwickelt. Erfahren Sie mehr über unseren Use-Case und unsere Mission.",
  );

  return (
    <div className="min-h-screen bg-stone-50 text-stone-900">
      <SiteNav />

      {/* Hero */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-12 text-center">
        <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-emerald-50 text-emerald-800 text-sm font-medium">
          <Lightbulb className="w-4 h-4" /> Unsere Geschichte
        </span>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold text-emerald-900 leading-[1.05] mt-6">
          Entstanden aus dem Praxisalltag.
        </h1>
        <p className="text-lg text-stone-600 mt-6 leading-relaxed max-w-2xl mx-auto">
          LAIzyDocu ist nicht am Reißbrett entstanden, sondern dort, wo Patient:innen täglich aufgenommen
          werden: am Empfangstresen einer Praxis. Genau deshalb passt es in den Alltag – statt ihn zu erschweren.
        </p>
      </section>

      {/* Story */}
      <section className="max-w-4xl mx-auto px-6 md:px-10 py-10">
        <div className="bg-white rounded-[2rem] border border-stone-100 shadow-[0_20px_60px_rgb(0,0,0,0.05)] p-8 md:p-12 space-y-6">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-emerald-900">Der Use-Case: weniger Papier, mehr Zeit für Patient:innen</h2>
          <p className="text-stone-700 text-lg leading-relaxed">
            In einer Physiotherapie-Praxis fällt bei jeder Neuaufnahme der gleiche Aufwand an: Datenschutzerklärung
            ausdrucken, vom Patienten lesen und unterschreiben lassen, einscannen, ablegen und über Jahre sicher
            archivieren. Das kostet Zeit, Papier und Nerven – und birgt immer das Risiko, dass ein Dokument fehlt,
            wenn es darauf ankommt.
          </p>
          <p className="text-stone-700 text-lg leading-relaxed">
            Aus genau diesem Bedarf heraus entstand die Idee zu LAIzyDocu. Statt einer Lösung „von außen“ haben wir
            sie <strong>gemeinsam mit einer Praxis</strong> entwickelt: Der QR-Code am Empfang, die mobile Unterschrift
            am Handy oder Tablet, die automatische PDF und die revisionssichere Ablage – jeder einzelne Schritt wurde
            am echten Patientenfluss erprobt und so lange verbessert, bis er sich nahtlos in den Praxisablauf einfügte.
          </p>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-emerald-900 max-w-2xl">Was uns antreibt</h2>
        <div className="grid md:grid-cols-3 gap-5 mt-10">
          {VALUES.map((v) => (
            <div key={v.title} data-testid={`about-value-${v.title}`} className="bg-white rounded-3xl border border-stone-100 p-7">
              <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-800 grid place-items-center mb-5">
                <v.icon className="w-6 h-6" />
              </div>
              <h3 className="font-display text-xl font-semibold text-emerald-900">{v.title}</h3>
              <p className="text-stone-600 mt-2 leading-relaxed">{v.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 pb-20">
        <div className="bg-emerald-900 text-white rounded-[2rem] p-10 md:p-14 text-center">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold max-w-2xl mx-auto">Lernen Sie LAIzyDocu in Ihrer Praxis kennen</h2>
          <p className="text-emerald-100/80 mt-4 text-lg max-w-xl mx-auto">Fordern Sie einen Testzugang an – kostenloser Testzeitraum auf Anfrage möglich.</p>
          <Link to="/testzugang" data-testid="about-cta" className="inline-block mt-8">
            <Button className="h-14 px-8 rounded-xl bg-white text-emerald-900 hover:bg-stone-100 text-base">
              Testzugang anfragen <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
}
